import { useMemo } from 'react';
import { useRouterState, useSearch } from '@tanstack/react-router';

import { useGetProductCatalogQuery } from '@/api/hooks/queries/use-get-product-catalog-query';
import { useGetProductCategoriesQuery } from '@/api/hooks/queries/use-get-product-categories-query';
import {
  PRODUCT_STEPS,
  type ProductStep,
  categoryIdsToLabels,
  getActiveProductSteps,
  getNextProductStepUrl,
} from '@/lib/products';
import type { TanstackUrl } from '@/lib/stepper';
import { getActionablePlanFamilyCarrierTypes } from '@/types/insurance-builder.type';
import { isInsuranceEligible } from '@/types/product.type';

import { useSessionStore } from './use-session-store/use-session-store';

const PRODUCT_SELECTION_PATHNAMES = new Set<string>(
  PRODUCT_STEPS.filter((step) => step.url !== '/products/summary').map((step) => step.url as string)
);

function nextStepSearchOverride(pathname: string, search: unknown): TanstackUrl | undefined {
  if (!PRODUCT_SELECTION_PATHNAMES.has(pathname)) return undefined;
  if (!search || typeof search !== 'object' || !('nextStep' in search)) return undefined;
  const raw = (search as { nextStep?: unknown }).nextStep;
  if (typeof raw !== 'string' || raw.length === 0) return undefined;
  return raw as TanstackUrl;
}

// Shared by navigation, the stepper and the empty-cart page so they can't disagree
export function useActiveProductSteps(): ProductStep[] {
  const selectedCategoryIds = useSessionStore((state) => state.selectedCategoryIds);
  const insuranceBuilder = useSessionStore((state) => state.insuranceBuilder);
  const { data: categories } = useGetProductCategoriesQuery();
  const { data: catalog, isSuccess: isCatalogLoaded } = useGetProductCatalogQuery();

  return useMemo(() => {
    const carrierTypes = getActionablePlanFamilyCarrierTypes(insuranceBuilder);

    return getActiveProductSteps({
      visibleLabels: new Set((categories ?? []).map((category) => category.label)),
      eligibleLabels: isCatalogLoaded
        ? new Set(
            (catalog ?? [])
              .filter((product) => isInsuranceEligible(product, carrierTypes))
              .map((product) => product.category.name)
          )
        : undefined,
      selectedLabels: new Set(categoryIdsToLabels(selectedCategoryIds, categories)),
    });
  }, [categories, catalog, isCatalogLoaded, insuranceBuilder, selectedCategoryIds]);
}

// Get the next step from the search params or the active steps for this patient
export function useProductSelectionStep(): {
  nextStep: TanstackUrl;
} {
  const activeSteps = useActiveProductSteps();
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  const searchOverride = useSearch({
    strict: false,
    shouldThrow: false,
    select: (search) => nextStepSearchOverride(pathname, search),
  });

  const sessionNext = getNextProductStepUrl(activeSteps);

  return useMemo(
    () => ({
      nextStep: (searchOverride ?? sessionNext) as TanstackUrl,
    }),
    [searchOverride, sessionNext]
  );
}
